// A properties panel that floats over a canvas, moved by its header and
// closed with its X.
//
// Owner, 2026-10-29: in Find in Sheets & Model the expanded 3D and 2D
// panes should be all canvas - "hides the bottom section, only allow the
// floating panel, properties, that can be moved or closed". One component
// for both panes, so the two cannot drift into two kinds of panel.
//
// Positioned against its pane (the nearest positioned ancestor) and kept
// inside it: a panel dragged off the edge cannot be dragged back.
import React, { useCallback, useEffect, useRef, useState } from "react";
import { CloseOutlined, HolderOutlined } from "@ant-design/icons";

const MARGIN = 8;

export function clampTo(pos, size, bounds) {
  const maxX = Math.max(MARGIN, bounds.w - size.w - MARGIN);
  const maxY = Math.max(MARGIN, bounds.h - size.h - MARGIN);
  return {
    x: Math.min(maxX, Math.max(MARGIN, pos.x)),
    y: Math.min(maxY, Math.max(MARGIN, pos.y)),
  };
}

export default function FloatingPanel({
  title, onClose, children,
  width = 340, maxHeight = "70%",
  // Where it opens, from the pane's top-right corner.
  initial = { right: 12, top: 52 },
  testId = "floating-panel",
}) {
  const ref = useRef(null);
  const [pos, setPos] = useState(null); // null until measured: opens at `initial`
  const drag = useRef(null);

  const bounds = useCallback(() => {
    const host = ref.current && ref.current.offsetParent;
    return host ? { w: host.clientWidth, h: host.clientHeight } : { w: window.innerWidth, h: window.innerHeight };
  }, []);

  const onDown = (e) => {
    if (e.button !== 0 || !ref.current) return;
    e.preventDefault();
    e.stopPropagation();
    drag.current = { sx: e.clientX, sy: e.clientY, x: ref.current.offsetLeft, y: ref.current.offsetTop };
  };

  useEffect(() => {
    const move = (e) => {
      const d = drag.current;
      if (!d || !ref.current) return;
      setPos(clampTo({ x: d.x + e.clientX - d.sx, y: d.y + e.clientY - d.sy },
        { w: ref.current.offsetWidth, h: ref.current.offsetHeight }, bounds()));
    };
    const up = () => { drag.current = null; };
    window.addEventListener("mousemove", move);
    window.addEventListener("mouseup", up);
    return () => { window.removeEventListener("mousemove", move); window.removeEventListener("mouseup", up); };
  }, [bounds]);

  const place = pos ? { left: pos.x, top: pos.y } : initial;
  return (
    <div
      ref={ref}
      data-testid={testId}
      // A click or a wheel on the panel is not a click on the model or a
      // pan of the sheet behind it.
      onMouseDown={(e) => e.stopPropagation()}
      onPointerDown={(e) => e.stopPropagation()}
      onWheel={(e) => e.stopPropagation()}
      style={{
        position: "absolute", zIndex: 20, width, maxWidth: `calc(100% - ${MARGIN * 2}px)`, maxHeight,
        display: "flex", flexDirection: "column", background: "#fff", borderRadius: 8,
        border: "1px solid #e5e7eb", boxShadow: "0 8px 24px rgba(0,0,0,.18)", ...place,
      }}
    >
      <div
        onMouseDown={onDown}
        title="Drag to move"
        style={{
          display: "flex", alignItems: "center", gap: 6, padding: "6px 8px 6px 10px",
          borderBottom: "1px solid #f0f0f0", cursor: "move", userSelect: "none",
          fontSize: 12.5, fontWeight: 600, background: "#fafafa", borderRadius: "8px 8px 0 0",
        }}
      >
        <HolderOutlined style={{ color: "#bbb" }} />
        <span style={{ flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{title}</span>
        {onClose && (
          <button
            type="button"
            aria-label="Close"
            title="Close"
            onMouseDown={(e) => e.stopPropagation()}
            onClick={onClose}
            style={{ border: "none", background: "transparent", cursor: "pointer", padding: 2, color: "#666", lineHeight: 1 }}
          >
            <CloseOutlined />
          </button>
        )}
      </div>
      <div style={{ overflow: "auto", minHeight: 0 }}>{children}</div>
    </div>
  );
}
